import { useState, useCallback, useRef } from "react";
import { loginUser, registerUser, forgotPassword, resetPassword, findEmail, getErrorMessage } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const signInWithGoogle = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function AuthPage({ onAuth }) {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", otp: "", newPassword: "" });
  const submittingRef = useRef(false); // Prevent double-submit

  const handleLogin = useCallback(async (e) => {
    e.preventDefault();
    if (submittingRef.current) return; // Block duplicate clicks
    if (!form.email || !form.password) { toast.error("Please enter email and password"); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      const data = await loginUser(form.email.trim(), form.password);
      toast.success("Welcome back!");
      onAuth(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  }, [form.email, form.password, onAuth]);

  const handleRegister = useCallback(async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!form.email || !form.password || !form.name) { toast.error("Please fill all fields"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      const data = await registerUser(form.email.trim(), form.password, form.name.trim());
      toast.success("Account created!");
      onAuth(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  }, [form.email, form.password, form.name, onAuth]);

  const handleForgotPassword = useCallback(async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!form.email) { toast.error("Enter your email"); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await forgotPassword(form.email.trim());
      toast.success(res.message || "Reset code sent to your email");
      setForm((prev) => ({ ...prev, otp: "" }));
      setMode("reset-otp");
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); submittingRef.current = false; }
  }, [form.email]);

  const handleResetPassword = useCallback(async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!form.otp || !form.newPassword) { toast.error("Fill OTP and new password"); return; }
    if (form.newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      await resetPassword(form.email.trim(), form.otp, form.newPassword);
      toast.success("Password reset! Please login.");
      setMode("login");
      setForm({ email: form.email, password: "", name: "", otp: "", newPassword: "" });
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); submittingRef.current = false; }
  }, [form.email, form.otp, form.newPassword]);

  const handleFindEmail = useCallback(async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;
    if (!form.name) { toast.error("Enter your name"); return; }
    submittingRef.current = true;
    setLoading(true);
    try {
      const res = await findEmail(form.name.trim());
      toast.success(`Your email: ${res.email_hint}`);
    } catch (err) { toast.error(getErrorMessage(err)); }
    finally { setLoading(false); submittingRef.current = false; }
  }, [form.name]);

  const switchMode = useCallback((newMode) => {
    setMode(newMode);
    setForm({ email: "", password: "", name: "", otp: "", newPassword: "" });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" style={{ minHeight: '100dvh' }} data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-gray-900 tracking-tight" style={{ fontFamily: "'Gupter', serif" }}>
            Huma <span className="text-indigo-600">Spices</span>
          </h1>
          <p className="text-gray-500 mt-2 text-sm">Professional CRM & Business Management</p>
        </div>

        <Card className="bg-white border border-gray-200 p-6 sm:p-8 shadow-sm" data-testid="auth-card">
          {mode === "login" && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Gupter', serif" }}>Sign In</h2>
              <Button type="button" variant="outline" onClick={signInWithGoogle} className="w-full h-12 text-base font-semibold border-2 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50 touch-target" data-testid="google-signin-btn">
                <GoogleLogo size={20} weight="bold" className="mr-2 text-indigo-600" /> Continue with Google
              </Button>
              <div className="flex items-center gap-3 my-5 text-xs text-gray-400">
                <span className="flex-1 h-px bg-gray-200" /> or sign in with email <span className="flex-1 h-px bg-gray-200" />
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                  <Input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="auth-email-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
                  <Input type="password" autoComplete="current-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Your password" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="auth-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 text-white font-bold touch-target" data-testid="auth-submit-btn">
                  {loading ? (
                    <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Signing in...</span>
                  ) : "Sign In"}
                </Button>
              </form>
              <div className="mt-4 space-y-2 text-center">
                <div className="flex justify-center gap-4">
                  <button type="button" onClick={() => setMode("forgot-password")} className="text-sm text-indigo-600 hover:text-indigo-800 touch-target" data-testid="forgot-password-link">Forgot Password?</button>
                  <button type="button" onClick={() => setMode("forgot-email")} className="text-sm text-indigo-600 hover:text-indigo-800 touch-target" data-testid="forgot-email-link">Forgot Email?</button>
                </div>
                <button type="button" onClick={() => switchMode("register")} className="text-sm text-gray-500 hover:text-gray-700 touch-target" data-testid="auth-toggle-btn">
                  Don't have an account? <span className="text-indigo-600 font-medium">Sign up</span>
                </button>
              </div>
            </>
          )}

          {mode === "register" && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Gupter', serif" }}>Create Account</h2>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Full Name</label>
                  <Input autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="auth-name-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                  <Input type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="auth-email-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
                  <Input type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="auth-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 text-white font-bold touch-target" data-testid="auth-submit-btn">
                  {loading ? <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Creating...</span> : "Create Account"}
                </Button>
                <p className="text-xs text-gray-500 text-center" data-testid="register-role-note">New accounts get Sales Team access. Your admin can change this.</p>
              </form>
              <div className="mt-4 text-center">
                <button type="button" onClick={() => switchMode("login")} className="text-sm text-gray-500 hover:text-gray-700 touch-target" data-testid="auth-toggle-btn">
                  Already have an account? <span className="text-indigo-600 font-medium">Sign in</span>
                </button>
              </div>
            </>
          )}

          {mode === "forgot-password" && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Gupter', serif" }}>Reset Password</h2>
              <p className="text-sm text-gray-500 mb-6">We'll email a 6-digit reset code to this address</p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Email Address</label>
                  <Input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@example.com" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="reset-email-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 text-white font-bold touch-target" data-testid="send-otp-btn">
                  {loading ? "Sending..." : "Email Me a Code"}
                </Button>
              </form>
              <button type="button" onClick={() => setMode("login")} className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 block mx-auto touch-target">Back to Sign In</button>
            </>
          )}

          {mode === "reset-otp" && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Gupter', serif" }}>Check Your Email</h2>
              <p className="text-sm text-gray-500 mb-6" data-testid="reset-code-sent-note">Enter the 6-digit code we sent to <b>{form.email}</b> (valid for 10 minutes) and choose a new password. Check your spam folder if you don't see it.</p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">OTP Code</label>
                  <Input inputMode="numeric" autoComplete="one-time-code" value={form.otp} onChange={(e) => setForm({ ...form, otp: e.target.value })} placeholder="6-digit OTP" className="h-12 text-base font-mono text-center tracking-widest" style={{ fontSize: '16px' }} data-testid="otp-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">New Password</label>
                  <Input type="password" autoComplete="new-password" value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} placeholder="Min 6 characters" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="new-password-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 text-white font-bold touch-target" data-testid="reset-password-btn">
                  {loading ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
              <button type="button" onClick={() => setMode("login")} className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 block mx-auto touch-target">Back to Sign In</button>
            </>
          )}

          {mode === "forgot-email" && (
            <>
              <h2 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Gupter', serif" }}>Find Your Email</h2>
              <p className="text-sm text-gray-500 mb-6">Enter the name used during registration</p>
              <form onSubmit={handleFindEmail} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Your Name</label>
                  <Input autoComplete="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="h-12 text-base" style={{ fontSize: '16px' }} data-testid="find-name-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 text-white font-bold touch-target" data-testid="find-email-btn">
                  {loading ? "Searching..." : "Find Email"}
                </Button>
              </form>
              <button type="button" onClick={() => setMode("login")} className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 block mx-auto touch-target">Back to Sign In</button>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
